/**
 * ChatRenderer — 공유 채팅 렌더링 컴포넌트
 * ChatPage와 SpaceDetailPage에서 공통으로 사용됩니다.
 * CSS: cbot-* (chat.css 기준)
 */

import React, { useState, useRef, useEffect } from 'react';
import SendIcon from '../../assets/icons/common/send.svg?react';
import { Button, Modal } from '../ui';
import { Workflow, ChatSource } from '../../api';
import type { ChatTheme } from '../../api/chat-sessions';
import { ChartBlock } from '../chart/ChartBlock';
import { MarkdownRenderer, extractChartsAndText } from './MarkdownRenderer';
import { ChartJsonBlock } from './ChartJsonBlock';
import { BotGlyph } from './BotGlyph';
import '../../styles/chat.css';

// ── 아이콘 ────────────────────────────────────────────────────────────────────
const ICON_PATHS = {
  plus:  <path d="M12 5v14M5 12h14" />,
  search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>,
  copy:  <><rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h8" /></>,
  redo:  <><path d="M21 12a9 9 0 1 1-2.6-6.4L21 8" /><path d="M21 3v5h-5" /></>,
  alert: <><circle cx="12" cy="12" r="9" /><path d="M12 7v6M12 16.5v.5" /></>,
  chev:  <path d="m6 9 6 6 6-6" />,
  code:  <path d="m8 7-5 5 5 5M16 7l5 5-5 5" />,
  msg:   <path d="M4 5h16v11H9l-5 4z" />,
  edit:  <><path d="M4 20h4L19 9l-4-4L4 16z" /><path d="m13.5 6.5 4 4" /></>,
  trash: <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />,
  check: <path d="m5 12 5 5 9-10" />,
};

function Icon({ name, size = 16 }: { name: keyof typeof ICON_PATHS; size?: number }) {
  return (
    <svg className="cbot-ic" width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {ICON_PATHS[name]}
    </svg>
  );
}

// 봇 아바타 (테마 색상 원형 배경)
function BotAvatar({ large = false }: { large?: boolean }) {
  return (
    <span className={`cbot-av${large ? ' cbot-av--lg' : ''}`}>
      <BotGlyph className="cbot-ic" size={large ? 30 : 16} />
    </span>
  );
}

// SQL 접힘 블록
function SqlBlock({ sql }: { sql: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="cbot-sql">
      <button type="button" className={`cbot-sql-toggle${open ? ' open' : ''}`} onClick={() => setOpen(o => !o)}>
        <Icon name="code" size={14} />실행된 SQL 보기<Icon name="chev" size={14} />
      </button>
      {open && <pre className="cbot-sql-pre">{sql}</pre>}
    </div>
  );
}

// ── 실행 트레이스 패널 (export — ChatPage에서도 사용) ─────────────────────────
export function TracePanel({ events, isLive = false }: { events: Array<Record<string, unknown>>; isLive?: boolean }) {
  const [open, setOpen] = useState(false);
  const shown = isLive || open;

  const renderEvent = (ev: Record<string, unknown>, i: number) => {
    const step = ev.step as string;
    const elapsed = ev.elapsed as string | undefined;
    const msg = ev.message as string | undefined;

    if (step === 'sql_generated') {
      const sql = ev.sql as string;
      const tokens = ev.tokens as { input: number; output: number } | undefined;
      return (
        <div key={i} className="cbot-trace-row">
          {elapsed && <span className="cbot-trace-time">[{elapsed}초]</span>}
          <span className="cbot-trace-label">▶ SQL 생성 완료</span>
          {tokens && <span className="cbot-trace-hint">(SQL생성: 입력 {tokens.input.toLocaleString()} / 출력 {tokens.output.toLocaleString()})</span>}
          <pre className="cbot-trace-sql">{sql}</pre>
        </div>
      );
    }
    if (step === 'rows_received') {
      return (
        <div key={i} className="cbot-trace-row">
          {elapsed && <span className="cbot-trace-time">[{elapsed}초]</span>}
          <span className="cbot-trace-label">▶ {msg}</span>
        </div>
      );
    }
    if (step === 'web_results') {
      const count = ev.count as number | undefined;
      return (
        <div key={i} className="cbot-trace-row">
          <span className="cbot-trace-label">{msg || `📄 검색 결과 ${count ?? 0}개 수신`}</span>
        </div>
      );
    }
    if (step === 'found') {
      const docs = ev.docs as Array<{ name: string; score: number }> | undefined;
      return (
        <div key={i} className="cbot-trace-row">
          {elapsed && <span className="cbot-trace-time">[{elapsed}초]</span>}
          <span className="cbot-trace-label">▶ {msg}</span>
          {docs && docs.length > 0 && (
            <div className="cbot-trace-docs">
              {docs.map((d, di) => (
                <div key={di} className="cbot-trace-doc-item">
                  <span className="cbot-trace-doc-score">{Math.min(Math.round(d.score * 100), 100)}%</span>
                  <span>{d.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }
    if (step === 'usage_detail') {
      const calls = ev.calls as Array<{ label: string; input: number; output: number }> | undefined;
      const totalInput = ev.totalInput as number | undefined;
      const totalOutput = ev.totalOutput as number | undefined;
      return (
        <div key={i} className="cbot-trace-row cbot-trace-usage">
          <span className="cbot-trace-label">[토큰]</span>
          {calls?.map((c, ci) => (
            <div key={ci} className="cbot-trace-token-line">
              [{ci + 1}] {c.label}: 입력 {c.input.toLocaleString()} / 출력 {c.output.toLocaleString()}
            </div>
          ))}
          {totalInput !== undefined && (
            <div className="cbot-trace-token-line cbot-trace-token-total">
              합계: 입력 {totalInput.toLocaleString()} / 출력 {(totalOutput ?? 0).toLocaleString()}
            </div>
          )}
        </div>
      );
    }
    return (
      <div key={i} className="cbot-trace-row">
        {elapsed && <span className="cbot-trace-time">[{elapsed}초]</span>}
        {msg && <span className="cbot-trace-label">{step === 'start' ? '' : '▶ '}{msg}</span>}
      </div>
    );
  };

  return (
    <div className="cbot-trace">
      {!isLive && (
        <button className="cbot-trace-toggle" onClick={() => setOpen(o => !o)}>
          {open ? '▲' : '▶'} 실행 트레이스 보기
        </button>
      )}
      {shown && (
        <div className={`cbot-trace-body${isLive ? ' cbot-trace-body--live' : ''}`}>
          {events.map((ev, i) => renderEvent(ev, i))}
          {isLive && <div className="cbot-trace-live-indicator">...</div>}
        </div>
      )}
    </div>
  );
}

// ── 출처 목록 (export — ChatPage에서도 사용) ──────────────────────────────────
export function SourceList({ sources, topK, hideSql = false }: {
  sources?: ChatSource[];
  topK?: number;
  hideSql?: boolean;
}) {
  if (!sources || sources.length === 0) return null;
  const sqlSources = hideSql ? [] : sources.filter(s => s.documentName === 'Generated SQL' && s.excerpt?.trim());
  const refSources = sources.filter(s => s.documentName !== 'Generated SQL');
  const uniqueRefSources = refSources.filter((s, i, arr) =>
    arr.findIndex(x => x.documentName === s.documentName) === i
  );
  if (sqlSources.length > 0) {
    return <>{sqlSources.map((s, i) => <SqlBlock key={i} sql={s.excerpt || ''} />)}</>;
  }
  if (uniqueRefSources.length === 0) return null;
  return (
    <div className="cbot-src">
      {uniqueRefSources.map((s, i) =>
        s.url ? (
          <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" title={s.url}>
            <em>출처</em>{s.documentName}
            {s.score > 0 && <span className="cbot-src-score">{Math.round(s.score * 100)}%</span>}
          </a>
        ) : (
          <span key={i}>
            <em>출처</em>{s.documentName}
            {s.score > 0 && <span className="cbot-src-score">{Math.round(s.score * 100)}%</span>}
          </span>
        )
      )}
      {topK !== undefined && (
        <span className="cbot-src-meta">Top {topK} · {uniqueRefSources.length}개 문서 참조</span>
      )}
    </div>
  );
}

// ── 타입 정의 ─────────────────────────────────────────────────────────────────

/** 세션 아이템 (ChatSessionItem, SpaceSession 공용) */
export interface SessionItem {
  sessionId: string;
  title?: string;
  startDateTime: string;
  userContent?: string;
}

/** 메시지 — ChatPage Message 확장 (spaceMode 발신자 정보 포함) */
export interface Message {
  id: string;
  chatId?: string;
  messageId?: string;
  role: 'user' | 'assistant';
  content: string;
  sources?: ChatSource[];
  chartData?: Record<string, unknown>[];
  usage?: {
    inputToken: number;
    outputToken: number;
    totalToken: number;
    costUsd?: number;
    costKrw?: number;
  };
  elapsedMs?: number;
  traceContent?: Array<Record<string, unknown>>;
  /** spaceMode 전용: 발신자 이름 */
  senderName?: string;
  /** spaceMode 전용: 발신자 이메일 (아바타 색상 결정) */
  senderEmail?: string;
}

/** ChatRenderer Props */
export interface ChatRendererProps {
  // ── 핵심 메시지 ──
  messages: Message[];
  typing: boolean;
  streamingContent: string;
  streamingTrace: Array<Record<string, unknown>>;
  streamingSources: ChatSource[];
  streamingChartData?: Record<string, unknown>[] | null;
  streamingStatus?: string;
  /** 메시지 전송 콜백 */
  onSend: (text: string) => void;
  messagesEndRef: React.RefObject<HTMLDivElement>;

  // ── 세션 사이드바 ──
  sessions: SessionItem[];
  currentSessionId: string | null;
  onNewChat: () => void;
  onSelectSession: (sessionId: string) => void;
  onDeleteSession: (sessionId: string) => void;
  onRenameSession: (sessionId: string, title: string) => void;

  // ── 봇 / 워크플로우 정보 ──
  botName: string;
  workflow?: Workflow | null;
  topK?: number;

  // ── 테마 (챗봇 전용, space는 기본값) ──
  activeTheme?: ChatTheme;

  // ── 빈 화면 퀵 프롬프트 ──
  quickPrompts?: string[];

  // ── 입력창 추가 요소 ──
  /** 입력창 앞에 삽입할 노드 (예: 파일 첨부 버튼) */
  composerPrefix?: React.ReactNode;
  /** 입력 비활성화 여부 (스페이스 archived 상태 등) */
  inputDisabled?: boolean;

  // ── 스페이스 모드 (optional) ──
  spaceMode?: boolean;
  /** 현재 사용자 이메일 — spaceMode에서 내 메시지 구분 */
  currentUserEmail?: string;
  /** 사이드바 하단에 삽입할 노드 (멤버 섹션 등) */
  sidebarBottom?: React.ReactNode;

  // ── 오른쪽 패널 (optional) ──
  /** 오른쪽에 추가 패널 (스페이스 컨텍스트 패널 등) */
  rightPanel?: React.ReactNode;
  /** 오른쪽 패널 너비 (px) */
  rightPanelWidth?: number;
  /** HTML 미리보기 버튼 클릭 콜백 (챗봇 전용) */
  onHtmlPreview?: (html: string | null) => void;
  /** 현재 열린 미리보기 HTML (버튼 활성 상태 표시용) */
  activePreviewHtml?: string | null;
  /** 모델 변경 콜백 (ChatPage에서 API 호출 시 modelOverride 용) */
  onModelChange?: (model: string) => void;
  /** cbot-layout div에 대한 외부 ref (드래그 리사이즈 성능 최적화용) */
  layoutRef?: React.RefObject<HTMLDivElement>;
}

// ── 유틸리티 ──────────────────────────────────────────────────────────────────

// 모델 이름 매핑
const MODEL_NAMES: Record<string, string> = {
  'apac.amazon.nova-lite-v1:0': 'Nova Lite',
  'apac.amazon.nova-micro-v1:0': 'Nova Micro',
  'apac.amazon.nova-pro-v1:0': 'Nova Pro',
  'global.amazon.nova-2-lite-v1:0': 'Nova 2 Lite',
  'apac.anthropic.claude-sonnet-4-20250514-v1:0': 'Claude Sonnet 4',
  'anthropic.claude-opus-5': 'Claude Opus 5',
  'anthropic.claude-sonnet-5': 'Claude Sonnet 5',
  'global.anthropic.claude-fable-5': 'Claude Fable 5',
  'global.anthropic.claude-fable-5-1': 'Claude Fable 5.1',
  'global.anthropic.claude-haiku-4-5-20251001-v1:0': 'Claude Haiku 4.5',
  'global.anthropic.claude-haiku-5-5': 'Claude Haiku 5.5',
  'global.anthropic.claude-opus-4-5-20251101-v1:0': 'Claude Opus 4.5',
  'global.anthropic.claude-opus-4-6-v1': 'Claude Opus 4.6',
  'global.anthropic.claude-opus-4-7': 'Claude Opus 4.7',
  'global.anthropic.claude-opus-4-8': 'Claude Opus 4.8',
  'global.anthropic.claude-opus-5-5': 'Claude Opus 5.5',
  'global.anthropic.claude-sonnet-4-6': 'Claude Sonnet 4.6',
  'global.anthropic.claude-sonnet-5-5': 'Claude Sonnet 5.5',
  'global.anthropic.claude-sonnet-4-5-20250514-v1:0': 'Claude Sonnet 4.5',
  'global.openai.gpt-5.4': 'GPT-5.4',
  'global.openai.gpt-5.5': 'GPT-5.5',
  'global.openai.gpt-6-sol': 'GPT-6 Sol',
};

const PROVIDER_NAMES: Record<string, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  bedrock: 'Bedrock',
};

function getModelName(modelId: string): string {
  if (!modelId) return '';
  if (MODEL_NAMES[modelId]) return MODEL_NAMES[modelId];
  const id = modelId.toLowerCase();
  if (id.includes('nova-2-lite'))       return 'Nova 2 Lite';
  if (id.includes('nova-lite'))         return 'Nova Lite';
  if (id.includes('nova-micro'))        return 'Nova Micro';
  if (id.includes('nova-pro'))          return 'Nova Pro';
  if (id.includes('claude-sonnet-4-5')) return 'Claude Sonnet 4.5';
  if (id.includes('claude-haiku-4-5'))  return 'Claude Haiku 4.5';
  if (id.includes('claude-opus-4-5'))   return 'Claude Opus 4.5';
  if (id.includes('claude-sonnet-4'))   return 'Claude Sonnet 4';
  if (id.includes('claude-haiku-4'))    return 'Claude Haiku 4';
  if (id.includes('claude-opus-4'))     return 'Claude Opus 4';
  if (id.includes('claude-sonnet-5'))   return 'Claude Sonnet 5';
  if (id.includes('claude-haiku-5'))    return 'Claude Haiku 5';
  if (id.includes('claude-opus-5'))     return 'Claude Opus 5';
  if (id.includes('claude-fable-5'))    return 'Claude Fable 5';
  if (id.includes('claude-sonnet'))     return 'Claude Sonnet';
  if (id.includes('claude-haiku'))      return 'Claude Haiku';
  if (id.includes('claude-opus'))       return 'Claude Opus';
  const gptM = id.match(/gpt-(\d+(?:\.\d+)?(?:-\w+)?)/);
  if (gptM) return `GPT-${gptM[1]}`;
  return modelId;
}

function getLLMConfig(workflow: Workflow | null) {
  if (!workflow) return { model: '', models: [] as string[], temperature: '0.7', provider: 'bedrock' };
  const llmNode = workflow.nodes?.find(n => n.type === 'llm');
  const rawModels = llmNode?.data?.models as string[] | undefined;
  const legacyModel = llmNode?.data?.model as string | undefined;
  const models: string[] = rawModels?.length ? rawModels : (legacyModel ? [legacyModel] : []);
  return {
    model: models[0] || '',
    models,
    temperature: (llmNode?.data?.temperature as string) || '0.7',
    provider: (llmNode?.data?.provider as string) || 'bedrock',
  };
}

// 테마 색상 대비 계산
const relLuminance = (color?: string): number | null => {
  const m = color?.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!m) return null;
  const hex = m[1].length === 3 ? m[1].replace(/./g, c => c + c) : m[1];
  const [r, g, b] = [0, 2, 4].map(i => {
    const v = parseInt(hex.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
const contrastRatio = (a: number, b: number) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
const DARK_TEXT_LUM = relLuminance('#333333')!;
const isDarkColor = (color?: string) => {
  const l = relLuminance(color);
  return l !== null && contrastRatio(l, 1) > contrastRatio(l, DARK_TEXT_LUM);
};
const isReadableOn = (text?: string, bg?: string) => {
  const t = relLuminance(text);
  const b = relLuminance(bg);
  return t === null || b === null || contrastRatio(t, b) >= 3;
};

const ERROR_PREFIX = '⚠️ 오류가 발생했습니다.';

// ── HTML 코드 추출 (코드블록 → iframe 미리보기용) ──────────────────────────────
function extractHtmlContent(content: string): string | null {
  const extractBlock = (lang: string): string | null => {
    const tag = `\`\`\`${lang}`;
    const idx = content.indexOf(tag);
    if (idx === -1) return null;
    const after = content.indexOf('\n', idx);
    const codeStart = after !== -1 ? after + 1 : idx + tag.length;
    const end = content.indexOf('```', codeStart);
    return (end !== -1 ? content.substring(codeStart, end) : content.substring(codeStart)).trim() || null;
  };
  const htmlPart = extractBlock('html');
  const cssPart  = extractBlock('css');
  const jsPart   = extractBlock('javascript') || extractBlock('js');
  if (htmlPart) {
    let combined = htmlPart
      .replace(/<script\s+src=["'][^"']*["'][^>]*><\/script>/gi, '')
      .replace(/<link\s[^>]*rel=["']stylesheet["'][^>]*>/gi, '');
    if (cssPart) combined = combined.includes('</head>')
      ? combined.replace('</head>', `<style>\n${cssPart}\n</style>\n</head>`)
      : `<style>\n${cssPart}\n</style>\n${combined}`;
    if (jsPart) combined = combined.includes('</body>')
      ? combined.replace('</body>', `<script>\n${jsPart}\n</script>\n</body>`)
      : `${combined}\n<script>\n${jsPart}\n</script>`;
    return combined;
  }
  if (cssPart || jsPart) {
    let combined = '<!DOCTYPE html><html><head></head><body></body></html>';
    if (cssPart) combined = combined.replace('</head>', `<style>\n${cssPart}\n</style>\n</head>`);
    if (jsPart)  combined = combined.replace('</body>', `<script>\n${jsPart}\n</script>\n</body>`);
    return combined;
  }
  if (content.includes('<!DOCTYPE html') || content.includes('<!doctype html') || /<html[\s>]/i.test(content)) {
    return content.trim();
  }
  return null;
}

// ── 히스토리 사이드바 ─────────────────────────────────────────────────────────
function ChatHistorySidebar({
  sessions, currentSessionId,
  onNewChat, onSelectSession, onDeleteSession, onRenameSession,
  sidebarBottom,
}: {
  sessions: SessionItem[];
  currentSessionId: string | null;
  onNewChat: () => void;
  onSelectSession: (sessionId: string) => void;
  onDeleteSession: (sessionId: string) => void;
  onRenameSession: (sessionId: string, title: string) => void;
  sidebarBottom?: React.ReactNode;
}) {
  const [query, setQuery] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const titleOf = (s: SessionItem) => s.title || s.userContent?.slice(0, 30) || '새 대화';

  const groupByDate = (list: SessionItem[]) => {
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    const groups: Record<string, SessionItem[]> = {};
    list.forEach(s => {
      const d = new Date(s.startDateTime).toDateString();
      const label = d === today ? '오늘' : d === yesterday ? '어제'
        : new Date(s.startDateTime).toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' });
      if (!groups[label]) groups[label] = [];
      groups[label].push(s);
    });
    return groups;
  };

  const q = query.trim().toLowerCase();
  const groups = groupByDate(q ? sessions.filter(s => titleOf(s).toLowerCase().includes(q)) : sessions);

  const highlight = (text: string) => {
    if (!q) return <>{text}</>;
    const idx = text.toLowerCase().indexOf(q);
    if (idx === -1) return <>{text}</>;
    return (
      <>
        {text.slice(0, idx)}
        <mark className="cbot-hist-highlight">{text.slice(idx, idx + q.length)}</mark>
        {text.slice(idx + q.length)}
      </>
    );
  };

  const submitRename = (id: string) => {
    if (renameValue.trim()) onRenameSession(id, renameValue.trim());
    setRenamingId(null);
  };

  return (
    <>
    <aside className="cbot-hist">
      <Button color="primary" className="cbot-hist-new" onClick={onNewChat}>
        <svg className="cbot-ic" width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
          <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
        </svg>
        새 대화
      </Button>
      <label className="cbot-hist-search">
        <svg className="cbot-ic" width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="대화 검색" />
      </label>
      <div className="cbot-hist-list">
        {Object.keys(groups).length === 0 ? (
          <div className="cbot-hist-empty">{q ? '검색 결과가 없습니다' : '대화 내역이 없습니다'}</div>
        ) : (
          Object.entries(groups).map(([label, items]) => (
            <div key={label} className="cbot-hist-group">
              <p className="cbot-hist-date">{label}</p>
              {items.map(s => (
                <div
                  key={s.sessionId}
                  className={`cbot-hist-item${currentSessionId === s.sessionId ? ' on' : ''}`}
                  onClick={() => renamingId !== s.sessionId && onSelectSession(s.sessionId)}
                >
                  {renamingId === s.sessionId ? (
                    <input
                      className="cbot-hist-rename"
                      value={renameValue}
                      autoFocus
                      onChange={e => setRenameValue(e.target.value)}
                      onBlur={() => submitRename(s.sessionId)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') submitRename(s.sessionId);
                        if (e.key === 'Escape') setRenamingId(null);
                      }}
                      onClick={e => e.stopPropagation()}
                    />
                  ) : (
                    <>
                      <b title={titleOf(s)}>{highlight(titleOf(s))}</b>
                      <span className="cbot-hist-actions">
                        <button type="button" title="이름 변경"
                          onClick={e => { e.stopPropagation(); setRenamingId(s.sessionId); setRenameValue(s.title || ''); }}>
                          <svg className="cbot-ic" width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                            <path d="M4 20h4L19 9l-4-4L4 16z" /><path d="m13.5 6.5 4 4" />
                          </svg>
                        </button>
                        <button type="button" className="del" title="삭제"
                          onClick={e => { e.stopPropagation(); setDeleteConfirmId(s.sessionId); }}>
                          <svg className="cbot-ic" width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                            <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
                          </svg>
                        </button>
                      </span>
                    </>
                  )}
                </div>
              ))}
            </div>
          ))
        )}
      </div>
      {sidebarBottom}
    </aside>

    {/* 대화 삭제 확인 모달 */}
    <Modal
      isOpen={!!deleteConfirmId}
      onClose={() => setDeleteConfirmId(null)}
      title="대화 삭제"
      size="sm"
      style={{ maxWidth: 360 }}
      bodyClassName="chat-confirm-body"
      footerRight={
        <>
          <Button color="gray" appearance="outline" onClick={() => setDeleteConfirmId(null)}>취소</Button>
          <Button color="error" onClick={() => {
            if (deleteConfirmId) { onDeleteSession(deleteConfirmId); }
            setDeleteConfirmId(null);
          }}>삭제</Button>
        </>
      }
    >
      <p style={{ margin: '4px 0 12px', lineHeight: 1.6, textAlign: 'center' }}>
        이 대화를 삭제하시겠습니까?<br />삭제 후 복구할 수 없습니다.
      </p>
    </Modal>
    </>
  );
}

// ── 메인 컴포넌트 ─────────────────────────────────────────────────────────────
export function ChatRenderer({
  messages,
  typing,
  streamingContent,
  streamingTrace,
  streamingSources,
  streamingChartData,
  streamingStatus,
  onSend,
  messagesEndRef,
  sessions,
  currentSessionId,
  onNewChat,
  onSelectSession,
  onDeleteSession,
  onRenameSession,
  botName,
  workflow,
  topK,
  activeTheme = {},
  quickPrompts = [],
  composerPrefix,
  inputDisabled = false,
  spaceMode = false,
  currentUserEmail,
  sidebarBottom,
  rightPanel,
  rightPanelWidth = 280,
  onHtmlPreview,
  activePreviewHtml,
  onModelChange,
  layoutRef,
}: ChatRendererProps) {
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [expandedSql, setExpandedSql] = useState<Set<string>>(new Set());

  // 입력창 높이 자동 조절
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [input]);

  const llm = getLLMConfig(workflow ?? null);
  const [selectedModel, setSelectedModel] = useState<string>('');
  const modelLabel = getModelName(selectedModel || llm.model);  const providerLabel = PROVIDER_NAMES[llm.provider] || llm.provider;
  const quickPromptsFiltered = quickPrompts.filter(p => p.trim());

  // 테마 파생값
  const darkBg = isDarkColor(activeTheme.backgroundColor);
  const botTextBg = activeTheme.botBubbleColor || activeTheme.backgroundColor || '#ffffff';
  const botTextColor = activeTheme.botTextColor && isReadableOn(activeTheme.botTextColor, botTextBg)
    ? activeTheme.botTextColor
    : activeTheme.botBubbleColor
      ? (isDarkColor(activeTheme.botBubbleColor) ? '#f1f1f4' : '#333333')
      : undefined;
  const userTextColor = activeTheme.userTextColor
    ? activeTheme.userTextColor
    : activeTheme.userBubbleColor
      ? (isDarkColor(activeTheme.userBubbleColor) ? '#f1f1f4' : '#333333')
      : undefined;

  // ── 내부 핸들러 ──
  const handleSendLocal = () => {
    if (!input.trim() || typing || inputDisabled) return;
    onSend(input.trim());
    setInput('');
  };

  const copyText = (key: string, text: string) => {
    navigator.clipboard?.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(k => (k === key ? null : k)), 1500);
  };

  const prevUserContent = (index: number): string | null => {
    for (let i = index - 1; i >= 0; i--) {
      if (messages[i].role === 'user') return messages[i].content;
    }
    return null;
  };

  // ── 답변 본문 렌더 ──
  const renderAnswerBody = (content: string, chartData?: Record<string, unknown>[] | null) => (
    <>
      <MarkdownRenderer content={content} className="cbot-answer-text" />
      {extractChartsAndText(content).chartGroups.map((specs, i) => (
        <ChartJsonBlock key={i} specs={specs} />
      ))}
      {chartData && chartData.length > 0 && <ChartBlock rows={chartData} />}
    </>
  );

  // ── 어시스턴트 메시지 렌더 ──
  const renderAssistant = (msg: Message, index: number) => {
    const retryText = prevUserContent(index);

    // 오류 응답
    if (msg.content.startsWith(ERROR_PREFIX)) {
      const detail = msg.content.slice(ERROR_PREFIX.length).trim();
      return (
        <>
          <div className="cbot-err">
            <p className="cbot-err-title"><Icon name="alert" />응답을 생성하지 못했습니다</p>
            <p className="cbot-err-desc">요청을 처리하는 중 오류가 발생했습니다. 잠시 후 다시 시도하거나 관리자에게 문의하세요.</p>
            {detail && (() => {
              const sqlSep = '\n\n실행된 SQL:\n';
              const sqlIdx = detail.indexOf(sqlSep);
              if (sqlIdx !== -1) {
                const errPart = detail.slice(0, sqlIdx);
                const sqlPart = detail.slice(sqlIdx + sqlSep.length);
                const isOpen = expandedSql.has(msg.id);
                return (
                  <>
                    <p className="cbot-err-detail">{errPart}</p>
                    <button type="button" className="cbot-err-sql-btn"
                      onClick={() => setExpandedSql(prev => {
                        const n = new Set(prev); n.has(msg.id) ? n.delete(msg.id) : n.add(msg.id); return n;
                      })}>
                      {isOpen ? '▲ 쿼리 닫기' : '▼ 실행된 쿼리 보기'}
                    </button>
                    {isOpen && <pre className="cbot-err-sql">{sqlPart}</pre>}
                  </>
                );
              }
              return <p className="cbot-err-detail">{detail}</p>;
            })()}
            <div className="cbot-err-row">
              {retryText && (
                <Button size="sm" color="gray" appearance="outline" disabled={typing} onClick={() => onSend(retryText)}>
                  <svg className="cbot-ic" width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M21 12a9 9 0 1 1-2.6-6.4L21 8"/><path d="M21 3v5h-5"/></svg>다시 시도
                </Button>
              )}
              <Button size="sm" color="gray" appearance="outline" onClick={() => copyText(`err-${msg.id}`, detail || msg.content)}>
                <svg className="cbot-ic" width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="9" y="9" width="12" height="12" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h8"/></svg>오류 복사
              </Button>
            </div>
          </div>
        </>
      );
    }

    // 정상 응답
    const meta: string[] = [];
    if (msg.elapsedMs !== undefined) meta.push(`${(msg.elapsedMs / 1000).toFixed(1)}s`);
    if (msg.usage) {
      meta.push(
        `InputToken : ${msg.usage.inputToken.toLocaleString()}, OutputToken : ${msg.usage.outputToken.toLocaleString()}, TotalToken : ${msg.usage.totalToken.toLocaleString()}`
        + (msg.usage.costUsd !== undefined ? `, 예상 비용 : $${msg.usage.costUsd.toFixed(4)} (약 ${(msg.usage.costKrw ?? 0).toLocaleString()}원)` : '')
      );
    }

    return (
      <>
        {renderAnswerBody(msg.content, msg.chartData)}
        {/* HTML 미리보기 버튼 (챗봇 전용, onHtmlPreview prop이 있을 때만) */}
        {(() => {
          if (!onHtmlPreview) return null;
          const extractedHtml = extractHtmlContent(msg.content);
          if (!extractedHtml) return null;
          const isActive = activePreviewHtml === extractedHtml;
          return (
            <div className="cbot-preview-btn-wrap">
              <button
                type="button"
                className={`cbot-preview-btn${isActive ? ' active' : ''}`}
                onClick={() => onHtmlPreview(isActive ? null : extractedHtml)}
              >
                <svg className="cbot-ic" width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d="m8 7-5 5 5 5M16 7l5 5-5 5" />
                </svg>
                HTML 미리보기
              </button>
            </div>
          );
        })()}
        <SourceList sources={msg.sources} topK={topK} hideSql={!!msg.traceContent?.length} />
        {msg.traceContent && msg.traceContent.length > 0 && (
          <TracePanel events={msg.traceContent} isLive={false} />
        )}
        <div className="cbot-acts">
          <button type="button" className="cbot-ib" title="클립보드 복사" onClick={() => copyText(msg.id, msg.content)}>
            <svg className="cbot-ic" width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              {copiedKey === msg.id
                ? <path d="m5 12 5 5 9-10" />
                : <><rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h8" /></>}
            </svg>
          </button>
          {retryText && (
            <button type="button" className="cbot-ib" title="다시 생성" disabled={typing} onClick={() => onSend(retryText)}>
              <svg className="cbot-ic" width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M21 12a9 9 0 1 1-2.6-6.4L21 8" /><path d="M21 3v5h-5" />
              </svg>
            </button>
          )}
          <span className="cbot-mono">{meta.join(' · ')}</span>
          {msg.chatId && (
            <span className="cbot-mono" style={{ marginLeft: 'auto' }}>Chat ID: #{msg.chatId}</span>
          )}
        </div>
      </>
    );
  };

  // ── 입력창 렌더 ──
  const renderComposer = (autoFocus = false) => (
    <>
      <div className="cbot-cmp">
        {composerPrefix}
        <textarea
          ref={inputRef}
          rows={1}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              handleSendLocal();
            }
          }}
          placeholder={`${botName}에게 질문하세요...`}
          disabled={typing || inputDisabled}
          autoFocus={autoFocus}
        />
        <button
          type="button"
          className="cbot-send"
          aria-label="보내기"
          title="보내기"
          onClick={handleSendLocal}
          disabled={!input.trim() || typing || inputDisabled}
        >
          <SendIcon />
        </button>
      </div>
      <div className="cbot-hint-row">
        {llm.models.length > 1 ? (
          <select
            value={selectedModel || llm.model}
            onChange={e => { setSelectedModel(e.target.value); onModelChange?.(e.target.value); }}
            className="select cbot-model-select"
          >
            {llm.models.map(mId => (
              <option key={mId} value={mId}>{getModelName(mId)}</option>
            ))}
          </select>
        ) : (
          <span className="cbot-model-badge">{modelLabel}</span>
        )}
        <p className="cbot-key-hint"><kbd>Enter</kbd> 보내기 / <kbd>Shift</kbd>+<kbd>Enter</kbd> 줄바꿈</p>
      </div>
    </>
  );

  // ── 그리드 컬럼 계산 ──
  const gridCols = rightPanel
    ? `260px minmax(0, 1fr) ${rightPanelWidth}px`
    : '260px minmax(0, 1fr)';

  // ── 렌더 ──
  return (
    <div
      ref={layoutRef}
      className={[
        'cbot-layout',
        activeTheme.botBubbleColor ? 'cbot-layout--bubble' : '',
        darkBg ? 'cbot-layout--dark' : '',
        spaceMode ? 'cbot-layout--space' : '',
      ].filter(Boolean).join(' ')}
      style={{
        '--accent':           activeTheme.primaryColor    || undefined,
        '--chat-bg':          activeTheme.backgroundColor || undefined,
        '--chat-user-bubble': activeTheme.userBubbleColor || undefined,
        '--chat-user-text':   userTextColor,
        '--chat-bot-bubble':  activeTheme.botBubbleColor  || undefined,
        '--chat-bot-text':    botTextColor,
        '--bot-icon-color':   activeTheme.botIconColor || activeTheme.primaryColor || undefined,
        '--bot-icon-fg':      activeTheme.botIconFgColor  || undefined,
        gridTemplateColumns: gridCols,
      } as React.CSSProperties}
    >
      {/* 좌측: 히스토리 사이드바 */}
      <ChatHistorySidebar
        sessions={sessions}
        currentSessionId={currentSessionId}
        onNewChat={onNewChat}
        onSelectSession={onSelectSession}
        onDeleteSession={onDeleteSession}
        onRenameSession={onRenameSession}
        sidebarBottom={sidebarBottom}
      />

      {/* 중앙: 채팅 영역 */}
      <section className="cbot-chat">
        {messages.length === 0 && !typing ? (
          /* 빈 화면 */
          <div className="cbot-empty">
            <BotAvatar large />
            <h2>{botName}</h2>
            <p className="cbot-empty-sub">{activeTheme.welcomeMessage || '무엇을 도와드릴까요?'}</p>
            <div className="cbot-chips">
              {providerLabel && <span className="cbot-chip">{providerLabel}</span>}
              {modelLabel && <span className="cbot-chip">{modelLabel}</span>}
              {llm.temperature && <span className="cbot-chip">Temperature {llm.temperature}</span>}
            </div>
            <div className="cbot-empty-cmp">{renderComposer(true)}</div>
            {quickPromptsFiltered.length > 0 && (
              <div
                className="cbot-faq"
                style={{ gridTemplateColumns: `repeat(${Math.min(quickPromptsFiltered.length, 4)}, minmax(0, 1fr))` }}
              >
                {quickPromptsFiltered.map((prompt, i) => (
                  <button key={i} type="button" className="cbot-faq-item" onClick={() => onSend(prompt)}>
                    <span className="cbot-faq-ic">
                      <svg className="cbot-ic" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                        <path d="M4 5h16v11H9l-5 4z" />
                      </svg>
                    </span>
                    <span className="cbot-faq-text">{prompt}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* 대화 중 */
          <>
            <div className="cbot-msgs">
              <div className="cbot-col">
                {messages.map((msg, index) =>
                  msg.role === 'user' ? (
                    spaceMode ? (
                      /* 스페이스 모드: 멀티유저 발신자 표시 */
                      <div key={msg.id} className={`cbot-ms-row${msg.senderEmail === currentUserEmail ? ' cbot-ms-row--me' : ''}`}>
                        <div
                          className="cbot-ms-av"
                          style={{ background: `hsl(${(msg.senderEmail || '').charCodeAt(0) * 37 % 360},60%,55%)` }}
                        >
                          {(msg.senderName || '?')[0]}
                        </div>
                        <div className="cbot-ms-body">
                          <div className="cbot-ms-meta">
                            <span className="cbot-ms-sender">{msg.senderName}</span>
                          </div>
                          <div className="cbot-ms-bubble">{msg.content}</div>
                        </div>
                      </div>
                    ) : (
                      /* 챗봇 모드: 오른쪽 정렬 버블 */
                      <div key={msg.id} className="cbot-mu">
                        <p>{msg.content}</p>
                      </div>
                    )
                  ) : (
                    <div key={msg.id} className="cbot-mb">
                      <BotAvatar />
                      <div className="cbot-mb-body">
                        {renderAssistant(msg, index)}
                      </div>
                    </div>
                  )
                )}

                {/* 스트리밍 중 */}
                {typing && (
                  <div className="cbot-mb">
                    <BotAvatar />
                    <div className="cbot-mb-body">
                      {streamingContent ? (
                        <>
                          {renderAnswerBody(streamingContent, streamingChartData)}
                          <SourceList sources={streamingSources} topK={topK} hideSql={streamingTrace.length > 0} />
                          {streamingTrace.length > 0 && (
                            <TracePanel events={streamingTrace} isLive={false} />
                          )}
                        </>
                      ) : streamingStatus ? (
                        <div className="cbot-typing-status">{streamingStatus}</div>
                      ) : streamingTrace.length > 0 ? (
                        <TracePanel events={streamingTrace} isLive={true} />
                      ) : (
                        <div className="cbot-typing"><span /><span /><span /></div>
                      )}
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </div>

            <div className="cbot-foot">{renderComposer()}</div>
          </>
        )}
      </section>

      {/* 우측 패널 (optional — 스페이스 컨텍스트 패널 등) */}
      {rightPanel && (
        <aside className="cbot-right-panel">
          {rightPanel}
        </aside>
      )}
    </div>
  );
}

export default ChatRenderer;
